import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, apiErr } from "../lib/api";
import { fmt } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { enablePush } from "../lib/push";
import { Receipt } from "../components/Receipt";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Bell, BellOff, Printer, Check, X, ChefHat, PackageCheck, Clock, LogOut, Volume2, Settings, Truck } from "lucide-react";

const COLUMNS = [
  { key: "new", title: "Nuovi", color: "#FF7A00" },
  { key: "confirmed", title: "In attesa pagamento", color: "#ef4444" },
  { key: "accepted", title: "Pagamento ricevuto", color: "#f59e0b" },
  { key: "preparing", title: "In preparazione", color: "#f59e0b" },
  { key: "ready", title: "Pronto per il ritiro", color: "#22c55e" },
  { key: "out_for_delivery", title: "In consegna", color: "#22c55e" },
  { key: "completed", title: "Completati", color: "#64748b" },
  { key: "closed", title: "Annullati/rifiutati", color: "#dc2626" },
];
const colOf = (s) => (["rejected", "cancelled"].includes(s) ? "closed" : s === "delivered" ? "completed" : s);

function paymentBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tone = (f, s, d) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime + s);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + s + d);
      o.start(ctx.currentTime + s); o.stop(ctx.currentTime + s + d + 0.02);
    };
    tone(660, 0, 0.15); tone(880, 0.16, 0.15); tone(1175, 0.32, 0.22);
    if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
  } catch (e) { /* audio off */ }
}

function useAlertSound(active, enabled) {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  useEffect(() => {
    const tone = (ctx, freq, start, dur) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime + start);
      g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur + 0.02);
    };
    const beep = () => {
      if (!enabled) return;
      try {
        if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = ctxRef.current;
        if (ctx.state === "suspended") ctx.resume();
        tone(ctx, 880, 0, 0.22);
        tone(ctx, 1180, 0.28, 0.22);
        if (navigator.vibrate) navigator.vibrate([250, 120, 250]);
      } catch (e) { console.debug("beep audio unavailable", e); }
    };
    if (active && enabled) { beep(); timerRef.current = setInterval(beep, 2000); }
    return () => clearInterval(timerRef.current);
  }, [active, enabled]);
}

function OrderCard({ o, onAction, onReview, onPrint, drivers, onAssign, now }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reviseOpen, setReviseOpen] = useState(false);
  const [ritems, setRitems] = useState(null);
  const [menu, setMenu] = useState([]);
  const [sharing, setSharing] = useState(false);
  const watchRef = useRef(null);
  const toggleShare = () => {
    if (sharing) { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; setSharing(false); return; }
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile"); return; }
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => { api.post(`/ops/orders/${o.id}/rider-location`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {}); },
      () => toast.error("Impossibile leggere la posizione"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 });
    setSharing(true);
    toast.success("Posizione condivisa con il cliente");
  };
  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); }, []);
  useEffect(() => {
    if (!reviseOpen) return;
    api.get(`/ops/menu?store_id=${o.store_id || ""}`).then((r) => setMenu(r.data)).catch(() => {});
  }, [reviseOpen, o.store_id]);
  const addProduct = (pid) => {
    const p = menu.find((x) => x.id === pid);
    if (!p) return;
    const base = (ritems || o.items).map((x) => ({ ...x }));
    const ex = base.find((x) => x.product_id === pid && (!x.options || !x.options.length));
    if (ex) ex.quantity += 1;
    else base.push({ product_id: p.id, name: p.name, quantity: 1, options: [], unit_price: Number(p.price) || 0 });
    setRitems(base);
  };
  const col = colOf(o.op_status);
  const act = (action, extra = {}) => onAction(o, action, extra);
  const changeQty = (i, d) => {
    const base = (ritems || o.items).map((x) => ({ ...x }));
    base[i] = { ...base[i], quantity: Math.max(0, (base[i].quantity || 0) + d) };
    setRitems(base);
  };
  const lineTotalOf = (it) => {
    const optSum = (it.options || []).reduce((s, o) => s + (Number(o.price) || 0), 0);
    return ((Number(it.unit_price) || 0) + optSum) * (it.quantity || 0);
  };
  const saveRevise = () => {
    const src = ritems || o.items;
    const items = src.filter((x) => x.quantity > 0).map((x) => ({ product_id: x.product_id, quantity: x.quantity, options: x.options || [] }));
    if (!items.length) { toast.error("L'ordine deve avere almeno un articolo"); return; }
    onReview(o, "revise", items); setReviseOpen(false); setRitems(null);
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      data-testid={`order-card-${o.short_code}`}
      className="bg-card rounded-lg border p-3 mb-2 shadow-sm" style={{ borderLeft: `4px solid ${COLUMNS.find(c => c.key === col)?.color}` }}>
      <div className="flex items-start justify-between">
        <span className="font-head font-extrabold text-lg">#{o.short_code}</span>
        <div className="text-right">
          <Badge variant="secondary" className={`text-[10px] ${o.pay_status === "paid" ? "bg-[hsl(var(--success))] text-white" : ""}`}>
            {o.pay_status === "paid" ? "PAGATO" : o.pay_status === "not_required" ? "AL RITIRO" : o.pay_status}
          </Badge>
          <p className="text-[11px] text-muted-foreground mt-1">{fmt.ago(o.created_at)}</p>
        </div>
      </div>
      <p className="text-sm font-medium mt-1">{o.customer?.name} · {o.customer?.phone}</p>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock size={11} /> Ritiro: {o.pickup_confirmed ? fmt.time(o.pickup_confirmed) : o.pickup_mode === "first" ? "prima disp." : fmt.time(o.pickup_at)}</p>
      <div className="mt-2 space-y-0.5">
        {o.items.map((it, i) => (
          <div key={i} className="text-sm">
            <div className="flex justify-between gap-2">
              <span className="font-semibold">{it.quantity}x {it.name}</span>
              <span className="text-muted-foreground shrink-0" data-testid={`op-item-total-${i}`}>{fmt.eur(it.line_total)}</span>
            </div>
            {it.options?.map((op, j) => <span key={j} className="text-[11px] text-muted-foreground block pl-3">{op.removable ? "− " : "+ "}{op.option_name}</span>)}
            {it.note && <span className="text-[11px] italic block pl-3">“{it.note}”</span>}
          </div>
        ))}
      </div>
      {o.note && <p className="text-xs bg-amber-50 border border-amber-200 rounded p-1.5 mt-2">Nota: {o.note}</p>}
      {o.order_type === "delivery" && (
        <div className="text-xs bg-cyan-50 border border-cyan-200 rounded p-1.5 mt-2">
          <div className="flex items-center gap-1 font-semibold text-cyan-800"><Truck size={12} /> {o.delivery?.address} · {o.delivery?.cap}</div>
          <div className="text-cyan-700">Consegna {fmt.eur(o.delivery_fee || 0)}</div>
          {["accepted", "preparing", "ready"].includes(o.op_status) && drivers && (
            <select data-testid={`assign-${o.short_code}`} value={o.assigned_driver_id || ""} onChange={(e) => onAssign(o, e.target.value)} className="mt-1 block w-full border rounded p-1 bg-white">
              <option value="">Assegna driver…</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {o.assigned_driver_name && <div className="mt-1 font-semibold">Driver: {o.assigned_driver_name}</div>}
        </div>
      )}
      {["confirmed", "accepted", "preparing", "ready", "out_for_delivery"].includes(o.op_status) && (() => {
        const map = { confirmed: ["#ef4444", "In attesa pagamento cliente"], accepted: ["#f59e0b", "Pagamento ricevuto"], preparing: ["#f59e0b", "In preparazione"], ready: ["#22c55e", "Pronto — il cliente può ritirare"], out_for_delivery: ["#22c55e", "In consegna"] };
        const [c, label] = map[o.op_status];
        return (
          <div className="flex items-center gap-2 mt-2 rounded-lg bg-muted/50 px-2.5 py-1.5" data-testid={`traffic-${o.short_code}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c, boxShadow: `0 0 0 3px ${c}33` }} />
            <span className="text-xs font-semibold">{label}</span>
          </div>
        );
      })()}
      <div className="flex justify-between items-center mt-2">
        <span className="font-bold">{fmt.eur(o.total)}</span>
        <button onClick={() => onPrint(o)} data-testid={`print-${o.short_code}`} className="text-muted-foreground hover:brand-text"><Printer size={16} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {o.op_status === "new" && o.pay_status === "pending" && o.created_at && (() => {
          const left = Math.max(0, Math.ceil((Date.parse(o.created_at) + 420000 - (now || Date.now())) / 1000));
          const mm = String(Math.floor(left / 60)).padStart(2, "0");
          const ss = String(left % 60).padStart(2, "0");
          const urgent = left > 0 && left <= 120;
          return (
            <div data-testid={`confirm-countdown-${o.short_code}`}
              className={`w-full text-xs font-bold text-center py-1 rounded mb-0.5 ${urgent ? "bg-red-100 text-red-700 animate-pulse" : "bg-amber-100 text-amber-700"}`}>
              {left > 0 ? `${urgent ? "⚠️ Sta per scadere! " : "⏳ "}Conferma entro ${mm}:${ss}` : "In annullamento…"}
            </div>
          );
        })()}
        {o.op_status === "new" && <>
          <Button size="sm" onClick={() => onReview(o, "confirm")} data-testid={`confirm-${o.short_code}`} className="brand-bg hover:opacity-90 h-8 flex-1"><Check size={14} className="mr-1" />Conferma</Button>
          <Button size="sm" variant="outline" onClick={() => { setRitems(null); setReviseOpen(true); }} data-testid={`revise-${o.short_code}`} className="h-8">Modifica</Button>
          <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)} data-testid={`reject-${o.short_code}`} className="h-8 text-destructive"><X size={14} /></Button>
        </>}
        {o.op_status === "confirmed" && <span className="text-xs font-semibold text-amber-600 flex-1 text-center py-1.5 bg-amber-50 rounded" data-testid={`awaiting-${o.short_code}`}>In attesa conferma cliente…</span>}
        {o.op_status === "accepted" && <Button size="sm" onClick={() => act("prepare")} className="h-8 flex-1" data-testid={`prepare-${o.short_code}`}><ChefHat size={14} className="mr-1" />Prepara</Button>}
        {o.op_status === "preparing" && <Button size="sm" onClick={() => act("ready")} className="h-8 flex-1 bg-[hsl(var(--success))] hover:opacity-90" data-testid={`ready-${o.short_code}`}><PackageCheck size={14} className="mr-1" />Pronto</Button>}
        {o.op_status === "ready" && o.order_type !== "delivery" && <Button size="sm" onClick={() => act("complete")} className="h-8 flex-1" data-testid={`complete-${o.short_code}`}>Completa</Button>}
        {o.op_status === "ready" && o.order_type === "delivery" && <Button size="sm" disabled={!o.assigned_driver_id} onClick={() => act("dispatch")} className="h-8 flex-1 bg-cyan-600 hover:opacity-90 disabled:opacity-40" data-testid={`dispatch-${o.short_code}`}>In consegna</Button>}
        {o.op_status === "out_for_delivery" && (
          <>
            <Button size="sm" onClick={toggleShare} data-testid={`share-loc-${o.short_code}`} className={`h-8 ${sharing ? "bg-red-500" : "bg-blue-600"} hover:opacity-90 text-white`}>{sharing ? "Stop posizione" : "Condividi posizione"}</Button>
            <Button size="sm" onClick={() => act("deliver")} className="h-8 flex-1 bg-[hsl(var(--success))] hover:opacity-90" data-testid={`deliver-${o.short_code}`}>Consegnato</Button>
          </>
        )}
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rifiuta ordine #{o.short_code}</DialogTitle><DialogDescription>Indica il motivo del rifiuto; il cliente verrà avvisato.</DialogDescription></DialogHeader>
          <Textarea placeholder="Motivazione (obbligatoria)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Annulla</Button>
            <Button className="bg-destructive hover:opacity-90" data-testid="reject-confirm"
              onClick={() => { if (!reason.trim()) return toast.error("Motivazione obbligatoria"); act("reject", { reason }); setRejectOpen(false); }}>Rifiuta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica ordine #{o.short_code}</DialogTitle><DialogDescription>Regola le quantità o rimuovi ciò che non è disponibile. Il cliente riceverà l'ordine aggiornato da confermare prima del pagamento.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(ritems || o.items).map((it, i) => (
              <div key={i} className={`flex items-center justify-between gap-2 border rounded-lg p-2 ${it.quantity === 0 ? "opacity-50" : ""}`} data-testid={`revise-item-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{it.name}</p>
                  <p className="text-[11px] text-muted-foreground" data-testid={`revise-item-total-${i}`}>{fmt.eur(lineTotalOf(it))}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button className="w-7 h-7 border rounded hover:bg-muted" onClick={() => changeQty(i, -1)} data-testid={`revise-minus-${i}`}>−</button>
                  <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                  <button className="w-7 h-7 border rounded hover:bg-muted" onClick={() => changeQty(i, 1)} data-testid={`revise-plus-${i}`}>+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-2 space-y-1 text-sm">
            {(() => {
              const sub = (ritems || o.items).reduce((s, it) => s + lineTotalOf(it), 0);
              const disc = (o.coupon && o.coupon.discount) || 0;
              const del = o.delivery_fee || 0;
              const loy = (o.loyalty_redeemed && o.loyalty_redeemed.amount) || 0;
              const final = Math.max(sub + del - disc - loy, 0);
              return (
                <>
                  <div className="flex justify-between" data-testid="revise-subtotal"><span>Totale prodotti</span><span className="font-semibold">{fmt.eur(sub)}</span></div>
                  {del > 0 && <div className="flex justify-between text-muted-foreground"><span>Consegna</span><span>{fmt.eur(del)}</span></div>}
                  {disc > 0 && <div className="flex justify-between text-[hsl(var(--success))]"><span>Sconto {o.coupon?.code}</span><span>-{fmt.eur(disc)}</span></div>}
                  {loy > 0 && <div className="flex justify-between text-[hsl(var(--success))]"><span>Punti fedeltà</span><span>-{fmt.eur(loy)}</span></div>}
                  <div className="flex justify-between border-t pt-1.5 mt-1 text-base font-bold" data-testid="revise-final-total"><span>Il cliente pagherà</span><span>{fmt.eur(final)}</span></div>
                </>
              );
            })()}
          </div>
          <div className="mt-2">
            <select className="w-full border rounded p-2 text-sm bg-white" data-testid={`revise-add-${o.short_code}`} value="" onChange={(e) => { if (e.target.value) { addProduct(e.target.value); e.target.value = ""; } }}>
              <option value="">+ Aggiungi prodotto…</option>
              {menu.map((p) => <option key={p.id} value={p.id}>{`${p.name} · €${Number(p.price).toFixed(2)}`}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReviseOpen(false); setRitems(null); }}>Annulla</Button>
            <Button className="brand-bg" data-testid={`revise-save-${o.short_code}`} onClick={saveRevise}>Proponi al cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

export default function OperatorDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "store_manager";
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("all");
  const [soundOn, setSoundOn] = useState(true);
  const [pushState, setPushState] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [printOrder, setPrintOrder] = useState(null);
  const [connected, setConnected] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(() => api.get("/ops/orders", { params: storeFilter !== "all" ? { store_id: storeFilter } : {} }).then((r) => setOrders(r.data)).catch(() => {}), [storeFilter]);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  useEffect(() => { load(); api.get("/ops/drivers").then((r) => setDrivers(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/tenant/stores").then((r) => setStores(r.data)).catch(() => {}); }, [isAdmin]);

  useEffect(() => {
    let es; let retry = 0; let closed = false; let timer;
    const connect = () => {
      if (closed) return;
      const url = `${API}/ops/stream${storeFilter !== "all" ? `?store_id=${storeFilter}` : ""}`;
      es = new EventSource(url, { withCredentials: true });
      es.onopen = () => { setConnected(true); retry = 0; };
      es.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "new_order") {
            setOrders((prev) => prev.find((o) => o.id === d.order.id) ? prev : [d.order, ...prev]);
            if (pushState === "granted") new Notification("Nuovo ordine #" + d.order.short_code, { body: `${d.order.customer?.name} · ${fmt.eur(d.order.total)}` });
          } else if (d.type === "update_order") {
            setOrders((prev) => {
              const old = prev.find((o) => o.id === d.order.id);
              if (old && old.op_status !== "accepted" && d.order.op_status === "accepted" && soundOnRef.current) {
                paymentBeep();
                toast.success(`Pagamento ricevuto — ordine #${d.order.short_code}, puoi preparare`);
              }
              return prev.map((o) => (o.id === d.order.id ? d.order : o));
            });
          }
        } catch (e) { console.debug("skipped malformed SSE frame", e); }
      };
      es.onerror = async () => {
        setConnected(false);
        es.close();
        if (closed) return;
        if (retry === 0) { try { await api.post("/auth/refresh"); } catch (e) { console.debug("token refresh failed (likely logged out)", e); } }
        retry += 1;
        timer = setTimeout(connect, Math.min(1000 * 2 ** retry, 15000));
      };
    };
    connect();
    return () => { closed = true; if (es) es.close(); if (timer) clearTimeout(timer); };
  }, [pushState, storeFilter]);

  const pendingNew = useMemo(() => orders.filter((o) => o.op_status === "new").length, [orders]);
  useAlertSound(pendingNew > 0, soundOn);

  const doAction = async (o, action, extra) => {
    try {
      const { data } = await api.post(`/ops/orders/${o.id}/transition`, { action, version: o.version, ...extra });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x)));
    } catch (e) { toast.error(apiErr(e)); load(); }
  };

  const print = (o) => { setPrintOrder(o); setTimeout(() => window.print(), 150); };

  const doAssign = async (o, driver_id) => {
    if (!driver_id) return;
    try { const { data } = await api.post(`/ops/orders/${o.id}/assign-driver`, { driver_id }); setOrders((prev) => prev.map((x) => (x.id === o.id ? data : x))); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const requestPush = async () => {
    try {
      await enablePush();
      setPushState("granted");
      toast.success("Notifiche attive: riceverai i nuovi ordini anche a schermo spento");
    } catch (e) {
      setPushState(typeof Notification !== "undefined" ? Notification.permission : "denied");
      toast.error(e?.message || "Impossibile attivare le notifiche");
    }
  };

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      enablePush({ requestPermission: false }).catch(() => {});
    }
  }, []);

  const grouped = COLUMNS.reduce((acc, c) => { acc[c.key] = orders.filter((o) => colOf(o.op_status) === c.key); return acc; }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-foreground text-white px-4 h-14 flex items-center justify-between sticky top-0 z-30 no-print">
        <div className="flex items-center gap-3">
          <span className="font-head font-bold">Cassa · Pickfood</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${connected ? "bg-green-500" : "bg-slate-500"}`}>{connected ? "Online" : "Riconnessione..."}</span>
          {pendingNew > 0 && <span className="text-[11px] bg-[var(--brand)] px-2 py-0.5 rounded-full font-bold alert-pulse" style={{ "--brand": "#FF7A00" }} data-testid="pending-badge">{pendingNew} da gestire</span>}
          {isAdmin && stores.length > 1 && (
            <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} data-testid="dash-store-filter"
              className="text-[12px] rounded-full bg-white/10 text-white border border-white/20 px-2 py-1 focus:outline-none">
              <option value="all" className="text-black">Tutte le sedi</option>
              {stores.map((s) => <option key={s.id} value={s.id} className="text-black">{s.name}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => setSoundOn(!soundOn)} data-testid="toggle-sound">{soundOn ? <Volume2 size={18} /> : <BellOff size={18} />}</Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={requestPush} data-testid="request-push">{pushState === "granted" ? <Bell size={18} /> : <BellOff size={18} />}</Button>
          {(user?.role === "tenant_admin" || user?.role === "store_manager") && <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => nav("/manage")}><Settings size={18} /></Button>}
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => { logout(); nav("/login"); }}><LogOut size={18} /></Button>
        </div>
      </header>

      {pushState !== "granted" && pushState !== "unsupported" && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-4 py-2 no-print">
          Le notifiche push sono disattivate. <button className="underline font-semibold" onClick={requestPush}>Attiva</button> per non perdere ordini (avviso sonoro e visivo restano attivi).
        </div>
      )}

      <div className="p-3 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-3 no-print">
        {COLUMNS.map((c) => (
          <div key={c.key} className="min-w-0">
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="font-head font-bold text-sm" style={{ color: c.color }}>{c.title}</h2>
              <span className="text-xs bg-white border rounded-full px-2">{grouped[c.key].length}</span>
            </div>
            <div className="min-h-[60px]">
              <AnimatePresence>
                {grouped[c.key].map((o) => <OrderCard key={o.id} o={o} now={nowTs} onAction={doAction} onReview={(o2, action, items) => api.post(`/ops/orders/${o2.id}/review`, { action, version: o2.version, items }).catch((e) => toast.error(apiErr(e)))} onPrint={print} drivers={drivers} onAssign={doAssign} />)}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      {printOrder && <Receipt order={printOrder} tenantName="Pickfood" storeName={user?.name} />}
    </div>
  );
}
